import { LocalStorageVariables } from "../entities/type";
import { UserType } from "../entities/profile";

/**
 * Per-tab customer session storage.
 *
 * The selected client's identity and tokens live in sessionStorage (per-tab
 * by spec) instead of localStorage, so each browser tab can have a different
 * client open without clobbering the others' customer token. All Nutrition
 * service `*BaseApiAffiliateRequest()` calls identify the client solely via
 * this token, so this key must never be shared between tabs.
 *
 * This module is the ONLY place that touches the storage key.
 */

type CustomerSessionListener = (client: UserType | null) => void;

const listeners = new Set<CustomerSessionListener>();

/**
 * Observe writes to this tab's customer session.
 *
 * Exists because the 401 interceptor in AppApiManager is a plain module: it can
 * refresh the customer token but cannot reach React state, so `ASSelectedClient`
 * would keep the pre-refresh token — and that is the copy the ElevenLabs widget
 * reads for its `secret__token` dynamic variable. AppStore subscribes and
 * mirrors the change. Returns an unsubscribe function.
 */
export const subscribeCustomerSession = (
  listener: CustomerSessionListener
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setCustomerSession = (client: UserType | null) => {
  if (client === null) {
    sessionStorage.removeItem(LocalStorageVariables.COSTUMER);
  } else {
    sessionStorage.setItem(
      LocalStorageVariables.COSTUMER,
      JSON.stringify(client)
    );
  }
  listeners.forEach((listener) => listener(client));
};

/**
 * Read the tab's customer session. When `expectedClientId` is given and the
 * stored client has a different id, returns null — guards against browsers
 * that copy sessionStorage into duplicated tabs.
 */
export const getCustomerSession = (
  expectedClientId?: string
): UserType | null => {
  const raw = sessionStorage.getItem(LocalStorageVariables.COSTUMER);
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as UserType | null;
    if (!parsed) {
      return null;
    }
    if (
      expectedClientId &&
      parsed.id != null &&
      String(parsed.id) !== String(expectedClientId)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Replace only the access token of the stored customer session (401 refresh).
 *
 * Reads the tab's session live rather than trusting a caller's snapshot, and
 * returns false when there is nothing to update — the session was cleared while
 * the request was in flight (logout, or a navigation that dropped the client),
 * and writing a snapshot back would resurrect it. Callers treat false as "do
 * not retry".
 */
export const updateCustomerToken = (token: string): boolean => {
  const current = getCustomerSession();
  if (!current) {
    return false;
  }
  setCustomerSession({ ...current, token });
  return true;
};
